import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import api from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (p) => p?.startsWith("http") ? p : `${BACKEND}${p}`;

export default function CoteSecrete() {
    const [teaser, setTeaser] = useState([]);
    const [email, setEmail] = useState("");
    const [addresses, setAddresses] = useState(null);
    const [memberInfo, setMemberInfo] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/cote-secrete/teaser").then(r => setTeaser(r.data)).catch(() => {});
        const saved = localStorage.getItem("cp_email");
        if (saved) {
            setEmail(saved);
            api.get(`/cote-secrete/${encodeURIComponent(saved)}`)
                .then(r => { setAddresses(r.data.addresses); setMemberInfo(r.data); })
                .catch(() => {});
        }
    }, []);

    async function unlock(e) {
        e?.preventDefault();
        if (!email) return;
        setLoading(true); setErr("");
        try {
            const r = await api.get(`/cote-secrete/${encodeURIComponent(email)}`);
            setAddresses(r.data.addresses);
            setMemberInfo(r.data);
            localStorage.setItem("cp_email", email);
        } catch (e) {
            if (e?.response?.status === 403) {
                setErr("Cet email n'est pas (encore) membre du Cercle. Rejoignez-nous ci-dessous.");
            } else {
                setErr("Erreur — réessayez.");
            }
        }
        setLoading(false);
    }

    return (
        <div data-testid="cote-secrete-page" className="bg-cp-ivoire">
            {/* HERO */}
            <section className="relative min-h-[85vh] bg-cp-encre text-cp-ivoire overflow-hidden cp-grain">
                <img src={`${BACKEND}/api/static/img/mon_club_key.jpg`} alt=""
                     className="absolute inset-0 w-full h-full object-cover cp-kenburns-slow cp-photo-cinematic"/>
                <div className="absolute inset-0 cp-hero-overlay"/>
                <div className="relative z-10 min-h-[85vh] max-w-[1600px] mx-auto px-5 sm:px-6 md:px-12 pt-32 md:pt-40 pb-20 flex flex-col justify-end">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
                        <p className="cp-tiny text-cp-sable mb-8 flex items-center gap-3">
                            <Lock size={13} strokeWidth={1.25} />
                            Réservé aux Membres du Cercle
                        </p>
                        <h1 className="cp-serif cp-hero-text text-[52px] sm:text-[84px] md:text-[132px] lg:text-[176px] leading-[0.88] tracking-[-0.03em] max-w-5xl">
                            Côte <em className="cp-display-italic text-cp-sable">Secrète.</em>
                        </h1>
                        <p className="cp-serif cp-hero-text italic text-base md:text-2xl font-light text-cp-ivoire/95 leading-[1.5] mt-8 max-w-3xl">
                            Cinq adresses que nous ne partageons qu'avec nos Membres. Contacts directs, tarifs négociés, confidentialité absolue. Ce que l'on transmet à un ami, pas à un guide.
                        </p>
                    </motion.div>
                </div>
            </section>

            {addresses ? (
                // ============ UNLOCKED VIEW ============
                <section className="py-24 md:py-32 max-w-[1400px] mx-auto px-5 sm:px-6 md:px-12" data-testid="cote-secrete-unlocked">
                    <div className="bg-cp-foret text-cp-ivoire px-6 md:px-10 py-5 mb-14 flex flex-wrap items-center justify-between gap-4">
                        <p className="cp-serif italic">
                            Bienvenue <em className="cp-display-italic text-cp-sable">Membre № {memberInfo?.member_number}</em>. Cinq adresses pour vous.
                        </p>
                        <button onClick={() => { setAddresses(null); setMemberInfo(null); localStorage.removeItem("cp_email"); setEmail(""); }} className="cp-tiny underline opacity-80">Déconnexion</button>
                    </div>

                    <div className="grid grid-cols-1 gap-16 md:gap-20">
                        {addresses.map((a, i) => (
                            <motion.article key={a.slug}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6 }}
                                data-testid={`secret-address-${a.slug}`}
                                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-start pb-16 border-b border-cp-border last:border-0">
                                <div className="md:col-span-5">
                                    <div className="cp-image-zoom aspect-[4/3] bg-cp-craie overflow-hidden">
                                        <img src={img(a.image)} alt={a.name} loading="lazy"
                                             className="w-full h-full object-cover cp-photo-cinematic"/>
                                    </div>
                                </div>
                                <div className="md:col-span-7">
                                    <span className="cp-tiny text-cp-terre">{a.city}</span>
                                    <h2 className="cp-serif text-4xl md:text-5xl lg:text-6xl leading-[1] text-cp-encre tracking-[-0.02em] mt-3 mb-3">
                                        <em className="cp-display-italic">{a.name}</em>
                                    </h2>
                                    <p className="cp-tiny text-cp-gris mb-5">{a.kind}</p>
                                    <p className="cp-serif text-lg md:text-xl leading-[1.55] text-cp-encre/90 mb-8">{a.description}</p>

                                    <div className="bg-cp-craie border-l-2 border-cp-terre pl-6 py-6 space-y-3">
                                        <p className="cp-tiny text-cp-terre">Contact direct — de la part de Côte Perdue</p>
                                        <p className="cp-serif text-lg">
                                            <em className="cp-display-italic">{a.contact_name}</em>
                                        </p>
                                        <p className="cp-serif text-lg text-cp-encre">
                                            <a href={`tel:${a.contact_phone.replace(/\s/g, "")}`} className="hover:text-cp-foret">{a.contact_phone}</a>
                                        </p>
                                        <p className="cp-serif text-lg text-cp-encre">
                                            <a href={`mailto:${a.contact_email}`} className="hover:text-cp-foret">{a.contact_email}</a>
                                        </p>
                                        <p className="cp-tiny text-cp-gris pt-2">À partir de {a.price_from}€ — tarif initié Côte Perdue</p>
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </div>

                    <div className="mt-20 bg-cp-encre text-cp-ivoire p-10 md:p-16 text-center">
                        <p className="cp-serif italic text-2xl md:text-3xl leading-snug">
                            Un devoir de <em className="cp-display-italic text-cp-sable">discrétion.</em><br />
                            Ces adresses ne circulent qu'entre Membres.
                        </p>
                    </div>
                </section>
            ) : (
                // ============ LOCKED / PAYWALL VIEW ============
                <>
                    {/* Teaser (blurred preview) */}
                    <section className="py-24 md:py-32 max-w-[1400px] mx-auto px-5 sm:px-6 md:px-12">
                        <p className="cp-tiny text-cp-terre mb-6">Aperçu</p>
                        <h2 className="cp-serif text-[44px] md:text-[72px] leading-[0.95] tracking-[-0.02em] text-cp-encre mb-16 max-w-3xl">
                            Cinq adresses <em className="cp-display-italic text-cp-foret">verrouillées.</em>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-4" data-testid="secret-teaser-grid">
                            {teaser.map((a, i) => (
                                <motion.div
                                    key={a.slug}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: i * 0.08 }}
                                    className="group">
                                    <div className="aspect-[3/4] bg-cp-craie overflow-hidden relative">
                                        <img src={img(a.image)} alt="" loading="lazy"
                                             className="w-full h-full object-cover cp-photo-cinematic blur-md group-hover:blur-sm transition-all duration-500 scale-105" />
                                        <div className="absolute inset-0 bg-cp-encre/30 flex items-center justify-center">
                                            <Lock size={22} strokeWidth={1.2} className="text-cp-ivoire/80" />
                                        </div>
                                    </div>
                                    <p className="cp-tiny text-cp-terre mt-4">{a.city}</p>
                                    <p className="cp-serif italic text-lg text-cp-encre mt-1">{a.kind.split(" · ")[0]}</p>
                                </motion.div>
                            ))}
                        </div>
                    </section>

                    {/* Paywall */}
                    <section className="bg-cp-encre text-cp-ivoire py-24 md:py-32 cp-grain" data-testid="cote-secrete-paywall">
                        <div className="max-w-3xl mx-auto px-6 md:px-12">
                            <p className="cp-tiny text-cp-sable mb-8 text-center">Déverrouiller</p>
                            <h2 className="cp-serif text-4xl md:text-6xl lg:text-7xl leading-[0.95] text-center mb-12">
                                Rejoignez le <em className="cp-display-italic text-cp-sable">Cercle.</em>
                            </h2>
                            <p className="cp-serif italic text-xl md:text-2xl text-cp-ivoire/85 leading-[1.5] text-center mb-14 max-w-2xl mx-auto">
                                À partir de 90€ / an, les cinq adresses ci-dessus vous sont ouvertes — avec tous les autres avantages du Cercle.
                            </p>

                            <div className="flex flex-col md:flex-row gap-5 mb-12 max-w-xl mx-auto">
                                <Link to="/club" className="cp-btn-outline-light flex-1 text-center" data-testid="secret-join-club">
                                    Voir les formules →
                                </Link>
                            </div>

                            <div className="border-t border-cp-ivoire/20 pt-10 max-w-xl mx-auto">
                                <p className="cp-tiny text-cp-sable mb-5 text-center">Déjà membre ?</p>
                                <form onSubmit={unlock} className="flex flex-col md:flex-row gap-3">
                                    <input type="email" placeholder="votre@email.com" value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="flex-1 px-4 py-3 bg-cp-ivoire/10 border border-cp-ivoire/20 focus:border-cp-sable outline-none cp-serif text-cp-ivoire placeholder:text-cp-ivoire/40"
                                        data-testid="secret-email-input"/>
                                    <button type="submit" disabled={loading}
                                        className="cp-btn-outline-light"
                                        data-testid="secret-unlock-btn">
                                        {loading ? "…" : "Déverrouiller"}
                                    </button>
                                </form>
                                {err && <p className="cp-tiny text-cp-sable mt-4 text-center" data-testid="secret-error">{err}</p>}
                            </div>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
